import React, { useState, useRef, useCallback, useEffect, memo } from "react";
import { motion, AnimatePresence, useInView  } from "framer-motion";

/* ══════════════════════════════════════════════
   KEYFRAMES — all looping via CSS (zero JS per frame)
══════════════════════════════════════════════ */
const STYLES = `
  @keyframes svcPulse   { 0%,100%{box-shadow:0 0 0 0 rgba(168,85,247,.42)} 70%{box-shadow:0 0 0 14px rgba(168,85,247,0)} }
  @keyframes glowPing   { 0%{transform:scale(1);opacity:.65} 100%{transform:scale(2.6);opacity:0} }
  @keyframes floatIcon  { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-7px)} }
  @keyframes orbitRing  { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
  @keyframes shimLine   { 0%{background-position:200% center} 100%{background-position:-200% center} }
  @keyframes tickIn     { from{opacity:0;transform:scale(0.5)} to{opacity:1;transform:scale(1)} }
  @keyframes blobPulse  { 0%,100%{transform:scale(1);opacity:.35} 50%{transform:scale(1.18);opacity:.6} }
  @keyframes countUp    { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
  @keyframes barFill    { from{width:0} to{width:var(--w)} }
  @keyframes dotBlink   { 0%,100%{opacity:1} 50%{opacity:.2} }
`;

/* ══════════════════════════════════════════════
   DATA
══════════════════════════════════════════════ */
const SERVICES = [
  { icon:"◈", title:"Shadow Architecture", tag:"Web Dev",     color:"#7c3aed",
    desc:"High-performance React & Next.js sites forged in precision — immersive, fast, and built to convert.",
    features:["React / Next.js","SSR + SSG","Performance","Mobile-First","SEO Ready","CI/CD"] },
  { icon:"◎", title:"Void Interfaces",     tag:"UI / UX",    color:"#a855f7",
    desc:"Minimal. Intentional. Dark interfaces that feel before they function — crafted for resonance.",
    features:["Figma Design","Dark Systems","Motion Design","Accessibility","Components","Style Guides"] },
  { icon:"⬡", title:"Night Systems",       tag:"React Apps", color:"#c084fc",
    desc:"Scalable stateful front-ends with real-time capability and composable architecture.",
    features:["Redux / Zustand","API Integration","WebSockets","Auth Flows","Code Split","Testing"] },
  { icon:"◆", title:"Obsidian Pages",      tag:"Landing",    color:"#e879f9",
    desc:"High-conversion pages wrapped in dark elegance, built on data-driven layout decisions.",
    features:["CRO Focused","A/B Ready","Analytics","GTM Setup","Fast Load","Lead Forms"] },
  { icon:"◉", title:"Silent Optimization", tag:"Perf + SEO", color:"#f43f5e",
    desc:"Invisible improvements that dramatically amplify your digital presence and rankings.",
    features:["Core Web Vitals","Lighthouse 100","Lazy Loading","Image CDN","Bundle Size","Cache"] },
  { icon:"◐", title:"Eclipse Deployments", tag:"DevOps",     color:"#ec4899",
    desc:"Zero-friction from dev to global — Docker, Vercel, AWS, done right.",
    features:["CI/CD Pipelines","Docker / K8s","Vercel / AWS","SSL & DNS","Monitoring","Rollbacks"] },
];

const PROCESS = [
  { n:"01", title:"Discovery Call",     color:"#7c3aed", icon:"◎",
    desc:"Deep-dive into your goals, audience, and competitive landscape to craft a precise roadmap." },
  { n:"02", title:"Dark Design System", color:"#a855f7", icon:"◈",
    desc:"Wireframes, moodboards, and dark component systems built with obsessive attention." },
  { n:"03", title:"Engineer & Animate", color:"#c084fc", icon:"⬡",
    desc:"Clean, performant code with Framer Motion animations and production-grade architecture." },
  { n:"04", title:"QA & Launch",        color:"#e879f9", icon:"◆",
    desc:"Cross-browser testing, Lighthouse audits, and zero-downtime deployment." },
  { n:"05", title:"Support & Scale",    color:"#f43f5e", icon:"◉",
    desc:"Post-launch monitoring, fixes, A/B testing, and performance iterations included." },
  { n:"06", title:"Grow & Iterate",     color:"#ec4899", icon:"◐",
    desc:"Analytics-driven decisions to keep your product sharp, fast, and ahead of the curve." },
];

/* Live capability bars — replaces pricing */
const CAPABILITIES = [
  { label:"UI / UX Design",          pct:97, color:"#7c3aed" },
  { label:"React Development",        pct:99, color:"#a855f7" },
  { label:"Animation & Motion",       pct:95, color:"#c084fc" },
  { label:"Performance Optimisation", pct:93, color:"#e879f9" },
  { label:"Backend & APIs",           pct:88, color:"#f43f5e" },
  { label:"DevOps & Deployment",      pct:90, color:"#ec4899" },
];

const RESULTS = [
  { metric:"+247%", label:"Average traffic increase",   icon:"◈", color:"#7c3aed" },
  { metric:"<1.2s", label:"Average load time achieved", icon:"⬡", color:"#a855f7" },
  { metric:"100",   label:"Lighthouse score delivered", icon:"◉", color:"#c084fc" },
  { metric:"3.8×",  label:"Conversion rate lift",       icon:"◆", color:"#e879f9" },
];

const FAQS = [
  { q:"How long does a project take?",           a:"Most projects ship in 1–3 weeks. We move fast without cutting corners." },
  { q:"Do you work with existing codebases?",    a:"Yes — we audit, refactor, and enhance existing React projects, not just greenfield builds." },
  { q:"What technologies do you specialise in?", a:"React, Next.js, Framer Motion, Node.js, MongoDB, and custom dark design systems." },
  { q:"Do you provide post-launch support?",     a:"Yes — 30-day post-launch support on every project, with optional monthly retainer packages." },
  { q:"How do revisions work?",                  a:"We iterate until you're thrilled. Most clients need 1–2 rounds; we never charge per revision." },
];

/* ══════════════════════════════════════════════
   SHARED UI ATOMS
══════════════════════════════════════════════ */
function Badge({ label }) {
  return (
    <div style={{ display:"inline-flex",alignItems:"center",gap:8,padding:"6px 18px",
      borderRadius:100,background:"rgba(124,58,237,0.1)",border:"1px solid rgba(124,58,237,0.28)",
      marginBottom:20 }}>
      <span style={{ width:6,height:6,borderRadius:"50%",background:"#a855f7",
        boxShadow:"0 0 8px #a855f7",display:"block",
        animation:"dotBlink 2s ease-in-out infinite",willChange:"opacity" }}/>
      <span style={{ fontSize:11,fontFamily:"'DM Sans',sans-serif",fontWeight:600,
        color:"#c084fc",letterSpacing:"0.12em",textTransform:"uppercase" }}>{label}</span>
    </div>
  );
}

function GradH({ children, size="clamp(34px,5vw,56px)" }) {
  return (
    <h2 style={{ fontFamily:"'Syne',sans-serif",fontWeight:800,margin:0,
      fontSize:size,lineHeight:1.05,letterSpacing:"-0.02em",color:"#f1f0f5" }}>
      {children}
    </h2>
  );
}

function Accent({ children }) {
  return (
    <span style={{ background:"linear-gradient(135deg,#a855f7,#e879f9)",
      WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",backgroundClip:"text" }}>
      {children}
    </span>
  );
}

/* Magnetic CTA — rAF throttled */
function MagBtn({ children, onClick, style }) {
  const ref = useRef(null);
  const raf = useRef(null);
  const onMove = useCallback((e) => {
    if (raf.current) return;
    raf.current = requestAnimationFrame(() => {
      if (!ref.current) { raf.current=null; return; }
      const r = ref.current.getBoundingClientRect();
      ref.current.style.transform =
        `translate(${(e.clientX-(r.left+r.width/2))*0.22}px,${(e.clientY-(r.top+r.height/2))*0.22}px)`;
      raf.current = null;
    });
  },[]);
  return (
    <button ref={ref} onClick={onClick} onMouseMove={onMove}
      onMouseLeave={()=>{ if(ref.current) ref.current.style.transform="translate(0,0)"; }}
      style={{ ...style,willChange:"transform",
        transition:"transform 0.4s cubic-bezier(0.16,1,0.3,1)" }}>
      {children}
    </button>
  );
}

/* ══════════════ SERVICE CARD — Framer Motion variant hover-reveal ══════════════ */
const ServiceCard = memo(function ServiceCard({ s, i }) {
  return (
    <motion.div initial={{ opacity:0, y:38 }} whileInView={{ opacity:1, y:0 }}
      viewport={{ once:true }} transition={{ duration:0.55, delay:i*0.07, ease:[0.16,1,0.3,1] }}>
      <motion.div whileHover="hovered" initial="idle"
        variants={{
          idle:    { y:0,  boxShadow:"none",                        borderColor:"rgba(124,58,237,0.13)" },
          hovered: { y:-8, boxShadow:`0 14px 60px ${s.color}20`,   borderColor:`${s.color}55`,
            transition:{ type:"spring", stiffness:260, damping:22 } },
        }}
        style={{
          padding:"36px 28px",borderRadius:22,height:"100%",cursor:"default",
          background:"rgba(255,255,255,0.025)",
          position:"relative",overflow:"hidden",willChange:"transform,box-shadow",
        }}>

        {/* Ambient top glow */}
        <motion.div variants={{ idle:{opacity:0}, hovered:{opacity:1} }} transition={{ duration:0.3 }}
          style={{ position:"absolute",top:-44,left:"50%",transform:"translateX(-50%)",
            width:170,height:170,borderRadius:"50%",pointerEvents:"none",
            background:`radial-gradient(circle,${s.color}28,transparent 70%)` }}/>
        {/* Top border strip */}
        <motion.div variants={{ idle:{opacity:0}, hovered:{opacity:1} }} transition={{ duration:0.3 }}
          style={{ position:"absolute",top:0,left:0,right:0,height:1,pointerEvents:"none",
            background:`linear-gradient(90deg,transparent,${s.color}aa,transparent)` }}/>
        {/* Corner TL */}
        <motion.div variants={{ idle:{opacity:0,scale:0.3}, hovered:{opacity:1,scale:1,transition:{duration:0.28,ease:[0.16,1,0.3,1]}} }}
          style={{ position:"absolute",top:0,right:0,width:40,height:40,pointerEvents:"none",
            borderTop:`2px solid ${s.color}`,borderRight:`2px solid ${s.color}`,borderRadius:"0 20px 0 0" }}/>
        {/* Corner BR */}
        <motion.div variants={{ idle:{opacity:0,scale:0.3}, hovered:{opacity:1,scale:1,transition:{duration:0.28,ease:[0.16,1,0.3,1]}} }}
          style={{ position:"absolute",bottom:0,left:0,width:40,height:40,pointerEvents:"none",
            borderBottom:`2px solid ${s.color}`,borderLeft:`2px solid ${s.color}`,borderRadius:"0 0 0 20px" }}/>
        {/* Icon */}
        <motion.div
          variants={{ idle:{scale:1,boxShadow:"none",rotate:0}, hovered:{scale:1.1,boxShadow:`0 0 28px ${s.color}55`,rotate:8,transition:{type:"spring",stiffness:300,damping:18}} }}
          style={{ width:52,height:52,borderRadius:14,marginBottom:20,
            background:`linear-gradient(135deg,${s.color}22,${s.color}09)`,
            border:`1px solid ${s.color}32`,
            display:"flex",alignItems:"center",justifyContent:"center",
            fontSize:24,color:s.color,willChange:"transform,box-shadow" }}>
          {s.icon}
        </motion.div>

        {/* Tag */}
        <div style={{ display:"inline-block",padding:"4px 12px",borderRadius:8,marginBottom:14,
          background:`${s.color}14`,border:`1px solid ${s.color}28`,
          fontFamily:"'DM Sans',sans-serif",fontSize:11,fontWeight:600,
          color:s.color,letterSpacing:"0.07em",textTransform:"uppercase" }}>{s.tag}</div>

        <h3 style={{ fontFamily:"'Syne',sans-serif",fontWeight:700,fontSize:20,
          color:"#f1f0f5",marginBottom:10,lineHeight:1.2 }}>{s.title}</h3>
        <p style={{ fontFamily:"'DM Sans',sans-serif",fontSize:14,
          color:"rgba(241,240,245,0.47)",lineHeight:1.78,marginBottom:22 }}>{s.desc}</p>

        {/* Feature chips — reveal via variants */}
        <motion.div
          variants={{ idle:{opacity:0,y:8}, hovered:{opacity:1,y:0,transition:{duration:0.3,delay:0.05}} }}
          style={{ display:"flex",flexWrap:"wrap",gap:7 }}>
          {s.features.map((f,fi)=>(
            <motion.span key={fi}
              variants={{ idle:{opacity:0,scale:0.85}, hovered:{opacity:1,scale:1,transition:{delay:fi*0.04,duration:0.25}} }}
              style={{ padding:"4px 10px",borderRadius:6,fontSize:11,
                fontFamily:"'DM Sans',sans-serif",fontWeight:500,
                background:"rgba(124,58,237,0.15)",border:"1px solid rgba(124,58,237,0.32)",
                color:"#c084fc" }}>{f}</motion.span>
          ))}
        </motion.div>
      </motion.div>
    </motion.div>
  );
});

/* ══════════════════════════════════════════════
   CAPABILITY BARS — animated on scroll
   Replaces pricing section — unique & glowing
══════════════════════════════════════════════ */
function CapBar({ c, i }) {
  const ref = useRef(null);
  const inView = useInView(ref,{ once:true,margin:"-50px" });
  const [animated,setAnimated] = useState(false);

  useEffect(()=>{ if(inView) setTimeout(()=>setAnimated(true),i*100); },[inView,i]);

  return (
    <motion.div ref={ref}
      initial={{ opacity:0,x:-20 }} whileInView={{ opacity:1,x:0 }}
      viewport={{ once:true }} transition={{ duration:0.55,delay:i*0.08,ease:[0.16,1,0.3,1] }}
      style={{ marginBottom:28 }}>
      <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10 }}>
        <div style={{ display:"flex",alignItems:"center",gap:10 }}>
          <div style={{ width:8,height:8,borderRadius:"50%",background:c.color,
            boxShadow:`0 0 10px ${c.color}` }}/>
          <span style={{ fontFamily:"'Syne',sans-serif",fontWeight:600,fontSize:15,
            color:"#f1f0f5" }}>{c.label}</span>
        </div>
        <motion.span
          initial={{ opacity:0 }} animate={animated?{ opacity:1 }:{}}
          transition={{ duration:0.4,delay:0.6+i*0.1 }}
          style={{ fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:16,
            background:`linear-gradient(135deg,${c.color},#e879f9)`,
            WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",backgroundClip:"text" }}>
          {c.pct}%
        </motion.span>
      </div>

      {/* Track */}
      <div style={{ height:6,borderRadius:100,background:"rgba(255,255,255,0.06)",
        position:"relative",overflow:"hidden" }}>
        {/* Animated fill */}
        <motion.div
          initial={{ width:0 }}
          animate={animated?{ width:`${c.pct}%` }:{}}
          transition={{ duration:1.2,delay:i*0.1,ease:[0.16,1,0.3,1] }}
          style={{ position:"absolute",top:0,left:0,height:"100%",borderRadius:100,
            background:`linear-gradient(90deg,${c.color},#e879f9)`,
            boxShadow:`0 0 14px ${c.color}90`,
            willChange:"width" }}/>
        {/* Shimmer overlay on the fill */}
        {animated && (
          <div style={{ position:"absolute",top:0,left:0,height:"100%",width:`${c.pct}%`,
            borderRadius:100,pointerEvents:"none",
            background:"linear-gradient(90deg,transparent 0%,rgba(255,255,255,0.25) 50%,transparent 100%)",
            backgroundSize:"200% 100%",
            animation:"shimLine 2s linear infinite",willChange:"background-position" }}/>
        )}
      </div>
    </motion.div>
  );
}

/* Animated orbital graphic alongside capability bars */
function CapOrbital() {
  return (
    <>
      <style>{`
        @keyframes orbitA { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes orbitB { from{transform:rotate(0deg)} to{transform:rotate(-360deg)} }
        @keyframes orbitC { from{transform:rotate(45deg)} to{transform:rotate(405deg)} }
      `}</style>
      <div style={{ position:"relative",width:260,height:260,margin:"0 auto",flexShrink:0 }}>
        {/* Blob bg */}
        <div style={{ position:"absolute",inset:0,borderRadius:"50%",
          background:"radial-gradient(circle,rgba(124,58,237,0.18) 0%,transparent 70%)",
          filter:"blur(24px)",
          animation:"blobPulse 4s ease-in-out infinite",willChange:"transform,opacity" }}/>

        {/* Ring A */}
        <div style={{ position:"absolute",inset:10,borderRadius:"50%",
          border:"1px solid rgba(124,58,237,0.22)",
          animation:"orbitA 18s linear infinite",willChange:"transform" }}>
          <div style={{ position:"absolute",top:-5,left:"50%",marginLeft:-5,
            width:10,height:10,borderRadius:"50%",
            background:"#7c3aed",boxShadow:"0 0 12px #7c3aed" }}/>
        </div>

        {/* Ring B */}
        <div style={{ position:"absolute",inset:30,borderRadius:"50%",
          border:"1px solid rgba(168,85,247,0.18)",
          animation:"orbitB 12s linear infinite",willChange:"transform" }}>
          <div style={{ position:"absolute",bottom:-4,left:"50%",marginLeft:-4,
            width:8,height:8,borderRadius:"50%",
            background:"#a855f7",boxShadow:"0 0 10px #a855f7" }}/>
        </div>

        {/* Ring C */}
        <div style={{ position:"absolute",inset:55,borderRadius:"50%",
          border:"1px solid rgba(232,121,249,0.15)",
          animation:"orbitC 8s linear infinite",willChange:"transform" }}>
          <div style={{ position:"absolute",top:-3,right:10,
            width:6,height:6,borderRadius:"50%",
            background:"#e879f9",boxShadow:"0 0 8px #e879f9" }}/>
        </div>

        {/* Core */}
        <div style={{ position:"absolute",inset:0,display:"flex",
          alignItems:"center",justifyContent:"center" }}>
          <div style={{ width:56,height:56,borderRadius:"50%",
            background:"linear-gradient(135deg,#7c3aed,#e879f9)",
            display:"flex",alignItems:"center",justifyContent:"center",
            boxShadow:"0 0 40px rgba(124,58,237,0.6)",
            fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:22,color:"#fff" }}>
            N
          </div>
        </div>
      </div>
    </>
  );
}

/* ══════════════════════════════════════════════
   RESULT CARD
══════════════════════════════════════════════ */
function ResultCard({ r, i }) {
  const ref = useRef(null);
  const inView = useInView(ref,{ once:true,margin:"-40px" });
  return (
    <motion.div ref={ref}
      initial={{ opacity:0,y:28 }} animate={inView?{ opacity:1,y:0 }:{}}
      transition={{ duration:0.6,delay:i*0.1,ease:[0.16,1,0.3,1] }}>
      <div style={{ padding:"36px 24px",borderRadius:20,textAlign:"center",
        background:`linear-gradient(145deg,${r.color}0e,rgba(255,255,255,0.02))`,
        border:`1px solid ${r.color}30`,
        boxShadow:`0 0 44px ${r.color}10`,
        position:"relative",overflow:"hidden" }}>
        <div style={{ position:"absolute",top:-30,left:"50%",transform:"translateX(-50%)",
          width:120,height:120,borderRadius:"50%",
          background:`radial-gradient(circle,${r.color}30,transparent 70%)`,
          filter:"blur(18px)",pointerEvents:"none" }}/>
        <div style={{ fontSize:22,color:r.color,marginBottom:14,
          textShadow:`0 0 18px ${r.color}`,position:"relative" }}>{r.icon}</div>
        <div style={{ fontFamily:"'Syne',sans-serif",fontWeight:800,
          fontSize:"clamp(28px,4vw,46px)",marginBottom:8,
          background:`linear-gradient(135deg,#f1f0f5,${r.color})`,
          WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",backgroundClip:"text",
          backgroundSize:"200% auto",
          animation:"shimLine 3s linear infinite",
          position:"relative" }}>{r.metric}</div>
        <div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:13,
          color:"rgba(241,240,245,0.43)",lineHeight:1.5,position:"relative" }}>{r.label}</div>
      </div>
    </motion.div>
  );
}

/* ══════════════════════════════════════════════
   PROCESS STEP
══════════════════════════════════════════════ */
function ProcessStep({ p, i }) {
  const ref = useRef(null);
  const inView = useInView(ref,{ once:true,margin:"-60px" });
  return (
    <motion.div ref={ref}
      initial={{ opacity:0,x:i%2===0?-28:28 }}
      animate={inView?{ opacity:1,x:0 }:{}}
      transition={{ duration:0.65,delay:i*0.09,ease:[0.16,1,0.3,1] }}
      style={{ display:"flex",gap:20,alignItems:"flex-start" }}>
      <div style={{ position:"relative",flexShrink:0 }}>
        <div style={{ width:52,height:52,borderRadius:"50%",
          background:`linear-gradient(135deg,${p.color},${p.color}bb)`,
          display:"flex",alignItems:"center",justifyContent:"center",
          fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:16,color:"#fff",
          boxShadow:`0 0 22px ${p.color}55`,
          animation:"svcPulse 2.8s ease-out infinite",willChange:"box-shadow" }}>{p.n}</div>
        <div style={{ position:"absolute",inset:0,borderRadius:"50%",
          border:`1px solid ${p.color}`,
          animation:"glowPing 2.4s ease-out infinite",willChange:"transform,opacity" }}/>
      </div>
      <div style={{ paddingTop:6 }}>
        <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:7 }}>
          <span style={{ fontSize:16,color:p.color }}>{p.icon}</span>
          <h4 style={{ fontFamily:"'Syne',sans-serif",fontWeight:700,fontSize:16,
            color:"#f1f0f5",margin:0 }}>{p.title}</h4>
        </div>
        <p style={{ fontFamily:"'DM Sans',sans-serif",fontSize:14,
          color:"rgba(241,240,245,0.46)",lineHeight:1.72,margin:0 }}>{p.desc}</p>
      </div>
    </motion.div>
  );
}

/* ══════════════════════════════════════════════
   FAQ
══════════════════════════════════════════════ */
function FAQItem({ q, a, i }) {
  const [open, setOpen] = useState(false);
  return (
    <motion.div initial={{ opacity:0,y:14 }} whileInView={{ opacity:1,y:0 }}
      viewport={{ once:true }} transition={{ duration:0.45,delay:i*0.06 }}>
      <div onClick={()=>setOpen(o=>!o)}
        style={{ padding:"20px 26px",borderRadius:14,cursor:"pointer",
          background:open?"rgba(124,58,237,0.09)":"rgba(255,255,255,0.025)",
          border:`1px solid ${open?"rgba(124,58,237,0.4)":"rgba(124,58,237,0.12)"}`,
          marginBottom:10,boxShadow:open?"0 0 28px rgba(124,58,237,0.1)":"none",
          transition:"all 0.28s cubic-bezier(0.16,1,0.3,1)" }}>
        <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",gap:16 }}>
          <span style={{ fontFamily:"'Syne',sans-serif",fontWeight:600,fontSize:15,
            color:open?"#c084fc":"#f1f0f5",transition:"color 0.25s ease" }}>{q}</span>
          <motion.span animate={{ rotate:open?45:0 }}
            transition={{ duration:0.22,ease:[0.16,1,0.3,1] }}
            style={{ color:open?"#a855f7":"rgba(241,240,245,0.35)",
              fontSize:22,flexShrink:0,lineHeight:1,display:"block" }}>+</motion.span>
        </div>
        <AnimatePresence initial={false}>
          {open && (
            <motion.div initial={{ height:0,opacity:0 }}
              animate={{ height:"auto",opacity:1 }}
              exit={{ height:0,opacity:0 }}
              transition={{ duration:0.3,ease:[0.16,1,0.3,1] }}
              style={{ overflow:"hidden" }}>
              <p style={{ fontFamily:"'DM Sans',sans-serif",fontSize:14,
                color:"rgba(241,240,245,0.5)",lineHeight:1.78,
                marginTop:14,marginBottom:0 }}>{a}</p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

/* ══════════════════════════════════════════════
   PAGE
══════════════════════════════════════════════ */
export default function Service() {
  return (
    <div className="service-page" style={{ minHeight:"100vh",padding:"120px 60px 80px",maxWidth:1200,margin:"0 auto" }}>
      <style>{STYLES}</style>

      {/* ── HEADER ── */}
      <motion.div initial={{ opacity:0,y:28 }} whileInView={{ opacity:1,y:0 }}
        viewport={{ once:true }} transition={{ duration:0.75 }}
        style={{ textAlign:"center",marginBottom:88 }}>
        <Badge label="What We Do" />
        <GradH size="clamp(42px,7vw,78px)">
          The Art of <Accent>Obsession</Accent>
        </GradH>
        <p style={{ fontFamily:"'DM Sans',sans-serif",fontWeight:300,fontSize:18,
          color:"rgba(241,240,245,0.48)",maxWidth:520,margin:"22px auto 0",lineHeight:1.8 }}>
          Six disciplines. One purpose. Every pixel deliberate, every interaction intentional.
        </p>
      </motion.div>

      {/* ── SERVICES GRID ── */}
      <div className="service-grid" style={{ display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:22,marginBottom:120 }}>
        {SERVICES.map((s,i)=><ServiceCard key={i} s={s} i={i}/>)}
      </div>

      {/* ── CAPABILITY BARS (replaces pricing) ── */}
      <motion.div initial={{ opacity:0 }} whileInView={{ opacity:1 }}
        viewport={{ once:true }} style={{ marginBottom:120 }}>
        <div style={{ textAlign:"center",marginBottom:64 }}>
          <Badge label="Expertise" />
          <GradH>What We <Accent>Master</Accent></GradH>
          <p style={{ fontFamily:"'DM Sans',sans-serif",fontWeight:300,fontSize:16,
            color:"rgba(241,240,245,0.42)",maxWidth:420,margin:"16px auto 0",lineHeight:1.75 }}>
            A living snapshot of our craft — refined across hundreds of real-world projects.
          </p>
        </div>
        <div className="service-capability-layout" style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 80px",
          padding:"52px 56px",borderRadius:28,alignItems:"center",
          background:"rgba(255,255,255,0.018)",border:"1px solid rgba(124,58,237,0.13)",
          boxShadow:"inset 0 0 80px rgba(124,58,237,0.04)",position:"relative",overflow:"hidden" }}>
          {/* Corner glows */}
          <div style={{ position:"absolute",top:-60,left:-60,width:220,height:220,borderRadius:"50%",
            background:"radial-gradient(circle,rgba(124,58,237,0.12),transparent 70%)",
            filter:"blur(32px)",pointerEvents:"none" }}/>
          <div style={{ position:"absolute",bottom:-60,right:-60,width:200,height:200,borderRadius:"50%",
            background:"radial-gradient(circle,rgba(232,121,249,0.1),transparent 70%)",
            filter:"blur(32px)",pointerEvents:"none" }}/>
          {/* Bars */}
          <div style={{ position:"relative",zIndex:1 }}>
            {CAPABILITIES.map((c,i)=><CapBar key={i} c={c} i={i}/>)}
          </div>
          {/* Orbital graphic */}
          <div style={{ position:"relative",zIndex:1,display:"flex",justifyContent:"center" }}>
            <CapOrbital />
          </div>
        </div>
      </motion.div>

      {/* ── RESULTS ── */}
      <motion.div initial={{ opacity:0 }} whileInView={{ opacity:1 }}
        viewport={{ once:true }} style={{ marginBottom:120 }}>
        <div style={{ textAlign:"center",marginBottom:60 }}>
          <Badge label="Results" />
          <GradH>Numbers That <Accent>Speak</Accent></GradH>
          <p style={{ fontFamily:"'DM Sans',sans-serif",fontWeight:300,fontSize:16,
            color:"rgba(241,240,245,0.42)",maxWidth:440,margin:"16px auto 0",lineHeight:1.75 }}>
            Average outcomes across client projects. Real results, real impact.
          </p>
        </div>
        <div className="service-results-grid" style={{ display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:18 }}>
          {RESULTS.map((r,i)=><ResultCard key={i} r={r} i={i}/>)}
        </div>
      </motion.div>

      {/* ── PROCESS ── */}
      <motion.div initial={{ opacity:0 }} whileInView={{ opacity:1 }}
        viewport={{ once:true }} style={{ marginBottom:120 }}>
        <div style={{ textAlign:"center",marginBottom:64 }}>
          <Badge label="Process" />
          <GradH>Six Steps to <Accent>Excellence</Accent></GradH>
        </div>
        <div style={{ padding:"52px 48px",borderRadius:28,position:"relative",overflow:"hidden",
          background:"rgba(255,255,255,0.018)",border:"1px solid rgba(124,58,237,0.12)",
          boxShadow:"inset 0 0 60px rgba(124,58,237,0.04)" }}>
          <div style={{ position:"absolute",top:-60,left:-60,width:200,height:200,borderRadius:"50%",
            background:"radial-gradient(circle,rgba(124,58,237,0.1),transparent 70%)",
            filter:"blur(30px)",pointerEvents:"none" }}/>
          <div style={{ position:"absolute",bottom:-60,right:-60,width:200,height:200,borderRadius:"50%",
            background:"radial-gradient(circle,rgba(232,121,249,0.09),transparent 70%)",
            filter:"blur(30px)",pointerEvents:"none" }}/>
          <div className="service-process-grid" style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:"38px 70px",
            position:"relative",zIndex:1 }}>
            {PROCESS.map((p,i)=><ProcessStep key={i} p={p} i={i}/>)}
          </div>
        </div>
      </motion.div>

      {/* ── FAQ ── */}
      <motion.div initial={{ opacity:0 }} whileInView={{ opacity:1 }}
        viewport={{ once:true }} style={{ marginBottom:80 }}>
        <div style={{ textAlign:"center",marginBottom:52 }}>
          <Badge label="FAQ" />
          <GradH>Common <Accent>Questions</Accent></GradH>
        </div>
        <div style={{ maxWidth:740,margin:"0 auto" }}>
          {FAQS.map((f,i)=><FAQItem key={i} {...f} i={i}/>)}
        </div>
      </motion.div>

      {/* ── BOTTOM CTA ── */}
      <motion.div initial={{ opacity:0,y:22 }} whileInView={{ opacity:1,y:0 }}
        viewport={{ once:true }} transition={{ duration:0.65 }}
        style={{ textAlign:"center" }}>
        <p style={{ fontFamily:"'DM Sans',sans-serif",fontSize:17,
          color:"rgba(241,240,245,0.4)",marginBottom:28 }}>
          Ready to engineer something unforgettable?
        </p>
        <MagBtn onClick={()=>window.location.href="/home/contact"} style={{
          padding:"16px 52px",borderRadius:14,
          background:"linear-gradient(135deg,#7c3aed,#a855f7)",
          border:"none",color:"#fff",
          fontFamily:"'DM Sans',sans-serif",fontWeight:700,fontSize:16,
          boxShadow:"0 0 44px rgba(124,58,237,0.5)",cursor:"pointer",
        }}>
          Start a Project →
        </MagBtn>
      </motion.div>

      <style>{`
        @media (max-width: 992px) {
          .service-page { padding: 110px 30px 70px !important; }
          .service-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .service-results-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .service-capability-layout { grid-template-columns: 1fr !important; gap: 30px !important; }
          .service-process-grid { grid-template-columns: 1fr !important; gap: 28px !important; }
        }
        @media (max-width: 768px) {
          .service-page { padding: 100px 20px 60px !important; }
        }
        @media (max-width: 580px) {
          .service-grid { grid-template-columns: 1fr !important; }
          .service-results-grid { grid-template-columns: 1fr !important; }
          .service-capability-layout { padding: 28px 18px !important; }
        }
        @media (max-width: 480px) {
          .service-page { padding: 94px 16px 56px !important; }
          .service-process-grid > div { gap: 14px !important; }
          .service-page button { width: 100%; }
        }
      `}</style>
    </div>
  );
}
